import { useState, type FormEvent } from 'react';
import { LegalLinks } from '../legal/LegalPage';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, API_URL } from '../../lib/api';
import { useAuth } from './AuthProvider';
import { Button, ErrorNote } from '../../components/ui';
import { Logo } from '../layout/Logo';

export function AuthPage({ mode }: { mode: 'login' | 'register' }) {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();
  const providers = useQuery({ queryKey: ['auth-providers'], queryFn: () => api<{ google: boolean }>('/api/auth/providers'), retry: false });
  const oauthError = params.get('error');
  const OAUTH_ERRORS: Record<string, string> = {
    google_cancelled: 'Google sign-in was cancelled.',
    google_not_configured: 'Google sign-in is not set up on this server yet.',
    google_email: 'Your Google account needs a verified email address.',
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await api(`/api/auth/${mode}`, { body: mode === 'login' ? { email, password } : { email, password, displayName: name || undefined } });
      await refresh();
      nav('/');
    } catch (err) { setError(err); } finally { setBusy(false); }
  };

  return (
    <div className="auth">
      <form className="auth__card" onSubmit={submit}>
        <Logo large />
        <h1>{mode === 'login' ? 'Welcome back' : 'Create your workspace'}</h1>
        <p className="muted">{mode === 'login' ? 'Sign in to continue your work.' : 'Conversations, artifacts and projects, saved in one place.'}</p>
        {providers.data?.google && (
          <>
            <a className="btn btn--secondary btn--md google-btn" href={`${API_URL}/api/auth/google/start`}>
              <svg viewBox="0 0 48 48" width="18" height="18" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
              <span>Continue with Google</span>
            </a>
            <div className="auth__or"><span>or</span></div>
          </>
        )}
        {oauthError && <div className="error-note">{OAUTH_ERRORS[oauthError] ?? 'Google sign-in failed. Please try again.'}</div>}
        {mode === 'register' && (
          <label className="field"><span>Name</span><input value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" /></label>
        )}
        <label className="field"><span>Email</span><input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" /></label>
        <label className="field"><span>Password</span><input type="password" required minLength={mode === 'register' ? 8 : 1} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete={mode === 'login' ? 'current-password' : 'new-password'} /></label>
        {mode === 'login' && <Link className="small" to="/forgot">Forgot password?</Link>}
        <ErrorNote error={error} />
        <Button variant="primary" type="submit" loading={busy}>{mode === 'login' ? 'Sign in' : 'Create account'}</Button>
        {mode === 'register' && <p className="small muted">By creating an account you agree to the <Link to="/legal/terms" target="_blank">Terms of Service</Link>, <Link to="/legal/acceptable-use" target="_blank">Acceptable Use Policy</Link> and <Link to="/legal/privacy" target="_blank">Privacy Policy</Link>.</p>}
        <p className="auth__switch muted">
          {mode === 'login' ? <>New here? <Link to="/register">Create an account</Link></> : <>Already have an account? <Link to="/login">Sign in</Link></>}
        </p>
      </form>
      <LegalLinks />
    </div>
  );
}

/** Email a reset code, then set a new password with it. */
export function ForgotPage() {
  const { refresh } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      if (!sent) { await api('/api/auth/forgot', { body: { email } }); setSent(true); }
      else { await api('/api/auth/reset', { body: { email, code, password } }); await refresh(); nav('/'); }
    } catch (err) { setError(err); } finally { setBusy(false); }
  };
  return (
    <div className="auth">
      <form className="auth__card" onSubmit={submit}>
        <Logo large />
        <h1>Reset your password</h1>
        <p className="muted">{sent ? <>If an account exists for <strong>{email}</strong>, we sent it a 6-digit code.</> : 'Enter your email and we will send you a code.'}</p>
        <label className="field"><span>Email</span><input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" disabled={sent} /></label>
        {sent && <>
          <label className="field"><span>Code</span><input required value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoComplete="one-time-code" autoFocus placeholder="123456" /></label>
          <label className="field"><span>New password</span><input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" /></label>
        </>}
        <ErrorNote error={error} />
        <Button variant="primary" type="submit" loading={busy}>{sent ? 'Set new password' : 'Send code'}</Button>
        <p className="auth__switch muted"><Link to="/login">Back to sign in</Link></p>
      </form>
      <LegalLinks />
    </div>
  );
}
